import Select, {
  components,
  type GroupBase,
  type MultiValueProps,
  type OptionProps,
  type StylesConfig,
} from "react-select";
import { getCustomSelectStyles } from "../../../Layouts/admin/shared";

export interface MultiSelectFilterOption {
  value: string;
  label: string;
}

/**
 * Cuántos chips se dibujan antes de resumir el resto en "+N". El combo vive en
 * la barra de filtros junto a los combos simples; sin tope, elegir varios patios
 * hace crecer la barra en alto y empuja la grilla hacia abajo.
 */
const MAX_VISIBLE_CHIPS = 2;

function CompactMultiValue(
  props: MultiValueProps<MultiSelectFilterOption, true, GroupBase<MultiSelectFilterOption>>,
) {
  const { index, selectProps } = props;
  if (index < MAX_VISIBLE_CHIPS) return <components.MultiValue {...props} />;
  if (index > MAX_VISIBLE_CHIPS) return null;
  const total = Array.isArray(selectProps.value) ? selectProps.value.length : 0;
  return (
    <span className="text-xs px-1.5 py-0.5 opacity-80 whitespace-nowrap">
      +{total - MAX_VISIBLE_CHIPS}
    </span>
  );
}

/**
 * Casilla a la izquierda de cada opción: con `hideSelectedOptions={false}` las
 * elegidas siguen en el menú, y sin la marca no se distingue cuáles ya están.
 * Un click sobre una elegida la quita (comportamiento propio de react-select).
 */
function CheckboxOption(
  props: OptionProps<MultiSelectFilterOption, true, GroupBase<MultiSelectFilterOption>>,
) {
  return (
    <components.Option {...props}>
      <span className="flex items-center gap-2">
        <i
          className={
            props.isSelected ? "ri-checkbox-fill text-primary-500" : "ri-checkbox-blank-line opacity-60"
          }
        ></i>
        <span>{props.label}</span>
      </span>
    </components.Option>
  );
}

/**
 * Combo de selección múltiple para las barras de filtros de Patios.
 *
 * Es el mismo `react-select` con `getCustomSelectStyles` que ya usan los
 * formularios de patios asignados (Vehículos, Grupos, Tarjetas), así que se ve
 * igual que el resto de la barra. Nada elegido = "todos": el placeholder lo
 * dice, y es lo que las páginas interpretan con `[]`.
 */
export default function MultiSelectFilter({
  isDarkMode,
  options,
  values,
  onChange,
  placeholder,
  noOptionsMessage,
  isDisabled = false,
  isClearable = true,
}: {
  isDarkMode: boolean;
  options: MultiSelectFilterOption[];
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  noOptionsMessage?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
}) {
  // Se resuelve contra `options` para respetar el orden de la selección y para
  // que un id que ya no se ofrece no quede como chip huérfano.
  const selected = values
    .map((v) => options.find((o) => o.value === v))
    .filter((o): o is MultiSelectFilterOption => !!o);

  return (
    <Select<MultiSelectFilterOption, true>
      isMulti
      options={options}
      value={selected}
      onChange={(opts) => onChange((opts || []).map((o) => o.value))}
      styles={
        getCustomSelectStyles(isDarkMode) as unknown as StylesConfig<
          MultiSelectFilterOption,
          true
        >
      }
      components={{ MultiValue: CompactMultiValue, Option: CheckboxOption }}
      placeholder={placeholder}
      // Se eligen varios de corrido: cerrar el menú en cada click obligaba a
      // reabrirlo por patio.
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable
      noOptionsMessage={() => noOptionsMessage || "Sin opciones"}
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      menuPosition="fixed"
    />
  );
}
